import { skipToken } from '@reduxjs/toolkit/query';
import { format } from 'date-fns';
import { AlertTriangle, CheckCircle2, Loader2, Pencil } from 'lucide-react';
import React, {
  type FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { CitationViewer } from '@/components/agent-chat/citations/CitationViewer';
import type { SelectedCitation } from '@/components/agent-chat/context/AgentChatContext';
import { useOptionalAgentChat } from '@/components/agent-chat/context/AgentChatContext';
import {
  NormalizedLorReviewRow,
  normalizeLorReviewRow,
  parseLorReviewTable,
  STATUS_STYLE_MAP,
} from '@/components/agent-chat/messages/lorUtils';
import { MessageContentViewer } from '@/components/agent-chat/messages/MessageContentViewer';
import {
  CitationPill,
  Citations,
} from '@/components/agent-chat/messages/tiptap-extensions/Citations';
import { DeliverableMatchCard } from '@/components/citations/DeliverableMatchCard';
import { buildClaimsFilingUrl } from '@/components/claims-filing/buildClaimsFilingUrl';
import { StepActionHeader } from '@/components/claims-filing/common/StepActionHeader';
import { ClaimFilingStep } from '@/components/claims-filing/hooks/useClaimFilingState';
import { getLatestCoverageTaskByCanonicalType } from '@/components/claims-filing/premises/premisesFlow';
import { useLorDocuments } from '@/components/claims-filing/lor/hooks/useLorData';
import { useLorPreview } from '@/components/claims-filing/lor/hooks/usePreview';
import { LorPreviewDialog } from '@/components/claims-filing/lor/LorPreviewDialog';
import { PremisesContactSelector } from '@/components/coverage/PremisesContactSelector';
import { RejectLorModal } from '@/components/coverage/RejectLorModal';
import { usePremisesDelivery } from '@/components/coverage/usePremisesDelivery';
import {
  SendFaxInlineCard,
  type SendFaxInlineCardRef,
} from '@/components/coverage/SendFaxInlineCard';
import { UploadedLorFileChip } from '@/components/coverage/UploadedLorFileChip';
import { FaxWizard } from '@/components/fax/wizard/FaxWizard';
import {
  SendFrontEmailInlineCard,
  type SendFrontEmailInlineCardRef,
} from '@/components/matter/SendFrontEmailDialog';
import { OnlyOfficeEditor } from '@/components/onlyoffice/OnlyOfficeEditor';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  RightDialogContent,
} from '@/components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  useCreateTaskUpdateMutation,
  useGetTasksQuery,
  useGetTaskStatusesQuery,
  useUpdateTaskMutation,
} from '@/services/api';
import {
  useAddLorToDocumentsMutation,
  useGetCoverageQuery,
} from '@/services/api/coverageService';
import { useGetTaskDeliverablesQuery } from '@/services/api/deliverablesService';
import {
  FaxTransmission,
  useListFaxTransmissionsByMatterQuery,
} from '@/services/api/faxService';
import {
  useGetMatterDetailsQuery,
  useGetMatterDocumentQuery,
} from '@/services/api/mattersService';
import {
  type EditorConfig,
  useLazyGetDocumentEditorConfigQuery,
} from '@/services/api/onlyofficeService';
import { TaskUpdateVisibility } from '@/services/types/client-intake-types';
import { ContactHandleSchema } from '@/services/types/coverage-types';
import {
  CanonicalTaskStatus,
  CanonicalTaskType,
} from '@/services/types/task-types';

import {
  PremisesDeliveryMethodCard,
  PremisesSendMethodCard,
} from './PremisesSendMethodCard';

/**
 * FlaggedFieldsSummary - Shows problematic LOR fields prominently with tooltips
 * Always visible when there are issues, helping reviewers spot problems quickly.
 */
const FlaggedFieldsSummary: React.FC<{
  rows: NormalizedLorReviewRow[];
  onViewAnalysis?: () => void;
}> = ({ rows, onViewAnalysis }) => {
  // Filter to only problematic fields
  const problematicFields = rows.filter(
    (row) =>
      row.status === 'EDIT' ||
      row.status === 'MISSING' ||
      row.status === 'VERIFY',
  );

  const matchingFields = rows.filter((row) => row.status === 'MATCH');

  if (problematicFields.length === 0 && matchingFields.length > 0) {
    // All fields match - show success state
    return (
      <div className="rounded-xl border border-green-200 bg-green-50/50 p-4">
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-green-100">
            <CheckCircle2 className="h-4 w-4 text-green-600" />
          </div>
          <div>
            <p className="text-sm font-medium text-green-800">
              All {matchingFields.length} fields verified
            </p>
            <p className="text-xs text-green-600">
              FinchGPT found no issues with the LOR fields
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (problematicFields.length === 0) {
    return null; // No review data available
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'MISSING':
        return (
          <span className="flex h-5 w-5 items-center justify-center rounded-full bg-red-100 text-red-600 text-xs font-bold">
            !
          </span>
        );
      case 'EDIT':
        return (
          <span className="flex h-5 w-5 items-center justify-center rounded-full bg-amber-100 text-amber-600 text-xs font-bold">
            ✎
          </span>
        );
      case 'VERIFY':
        return (
          <span className="flex h-5 w-5 items-center justify-center rounded-full bg-blue-100 text-blue-600 text-xs font-bold">
            ?
          </span>
        );
      default:
        return null;
    }
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'MISSING':
        return 'bg-red-100 text-red-700 border-red-200';
      case 'EDIT':
        return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'VERIFY':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      default:
        return 'bg-zinc-100 text-zinc-700 border-zinc-200';
    }
  };

  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50/50 p-4">
      <div className="flex items-start gap-3">
        <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-amber-100">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <p className="text-sm font-medium text-amber-800">
              {problematicFields.length} field
              {problematicFields.length !== 1 ? 's' : ''} flagged for review
            </p>
            {onViewAnalysis && (
              <button
                type="button"
                onClick={onViewAnalysis}
                className="text-xs font-medium text-amber-700 hover:text-amber-900 underline"
              >
                View full analysis
              </button>
            )}
          </div>
          <TooltipProvider delayDuration={200}>
            <div className="mt-3 flex flex-wrap gap-2">
              {problematicFields.map((field) => (
                <Tooltip key={field.id}>
                  <TooltipTrigger asChild>
                    <div
                      className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-medium cursor-help transition-colors hover:opacity-80 ${getStatusBadgeClass(field.status)}`}
                    >
                      {getStatusIcon(field.status)}
                      <span className="max-w-[150px] truncate">
                        {field.field}
                      </span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent
                    side="bottom"
                    className="max-w-xs bg-zinc-900 text-white p-3"
                  >
                    <div className="space-y-1.5">
                      <div className="flex items-center gap-2">
                        <span
                          className={`inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold ${getStatusBadgeClass(field.status)}`}
                        >
                          {field.status}
                        </span>
                        <span className="font-medium">{field.field}</span>
                      </div>
                      {field.notes && field.notes !== '—' && (
                        <p className="text-xs text-zinc-300">{field.notes}</p>
                      )}
                      <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[11px] pt-1 border-t border-zinc-700">
                        <div>
                          <span className="text-zinc-400">Provided:</span>
                          <p className="text-zinc-200 truncate">
                            {field.provided}
                          </p>
                        </div>
                        <div>
                          <span className="text-zinc-400">Expected:</span>
                          <p className="text-zinc-200 truncate">
                            {field.authoritative}
                          </p>
                        </div>
                      </div>
                    </div>
                  </TooltipContent>
                </Tooltip>
              ))}
            </div>
          </TooltipProvider>
          {matchingFields.length > 0 && (
            <p className="mt-2 text-xs text-amber-600">
              {matchingFields.length} other field
              {matchingFields.length !== 1 ? 's' : ''} verified ✓
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

const LorReviewComparisonTable: React.FC<{
  rows: NormalizedLorReviewRow[];
  matterId: string;
  setSelectedCitation?: (citation: SelectedCitation | null) => void;
}> = ({ rows, matterId, setSelectedCitation }) => {
  return (
    <div className="rounded-2xl border border-zinc-100 bg-white p-4 shadow-lg shadow-zinc-200/60">
      <div className="border-b border-zinc-100 px-2 pb-3">
        <span className="text-sm font-semibold text-zinc-700">
          FinchGPT Field Comparison
        </span>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50/80 text-xs uppercase text-zinc-500">
            <tr>
              <th className="px-4 py-2 text-left font-medium">Field</th>
              <th className="px-4 py-2 text-left font-medium">Provided</th>
              <th className="px-4 py-2 text-left font-medium">Authoritative</th>
              <th className="px-4 py-2 text-left font-medium">Status</th>
              <th className="px-4 py-2 text-left font-medium">Notes</th>
              <th className="px-4 py-2 text-left font-medium">Citations</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const statusClass =
                STATUS_STYLE_MAP[row.status] ?? STATUS_STYLE_MAP.DEFAULT;
              return (
                <tr
                  key={row.id}
                  className="border-t border-zinc-100 bg-white align-top text-zinc-700"
                >
                  <td className="px-4 py-3 font-medium">{row.field}</td>
                  <td className="px-4 py-3">{row.provided}</td>
                  <td className="px-4 py-3">{row.authoritative}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold ${statusClass}`}
                    >
                      {row.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">{row.notes}</td>
                  <td className="px-4 py-3">
                    {row.citations.length === 0 ? (
                      <span className="text-zinc-400">—</span>
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {row.citations.map((citation, index) => {
                          if (
                            citation.kind === 'modern' &&
                            setSelectedCitation
                          ) {
                            return (
                              <CitationPill
                                key={`${row.id}-citation-${citation.id}-${index}`}
                                citationId={citation.id}
                                matterId={matterId}
                                setSelectedCitation={setSelectedCitation}
                              />
                            );
                          }
                          // Legacy citations are stripped but not rendered as badges
                          if (citation.kind === 'legacy') {
                            return null;
                          }
                          if (citation.kind === 'text') {
                            return (
                              <span
                                key={`${row.id}-text-${citation.label}-${index}`}
                                className="inline-flex items-center rounded border border-zinc-200 bg-zinc-100 px-2 py-0.5 text-xs font-mono"
                              >
                                {citation.label}
                              </span>
                            );
                          }
                          return null;
                        })}
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ... rest of the file remains the same ...
type RouteParams = {
  matter_id: string;
  coverage_id: string;
};

export const ReviewSendLorPage: FC<{ hideHeader?: boolean }> = ({
  hideHeader = false,
}) => {
  const { matter_id: matterId, coverage_id: coverageId } =
    useParams<RouteParams>();
  const agentChatContext = useOptionalAgentChat();
  const [standaloneCitation, setStandaloneCitation] =
    useState<SelectedCitation | null>(null);

  const selectedCitation =
    agentChatContext?.selectedCitation ?? standaloneCitation;

  const handleSelectCitation = useCallback(
    (citation: SelectedCitation | null) => {
      setStandaloneCitation(citation);
      agentChatContext?.setSelectedCitation?.(citation);
    },
    [agentChatContext],
  );
  const [selectedDocId, setSelectedDocId] = useState<string | null>(null);
  const [isPreviewDialogOpen, setIsPreviewDialogOpen] = useState(false);
  const [isFaxWizardOpen, setIsFaxWizardOpen] = useState(false);
  const [showAnalysis, setShowAnalysis] = useState(false);

  // OnlyOffice editing state
  const [editorConfig, setEditorConfig] = useState<EditorConfig | null>(null);
  const [getDocumentEditorConfig] = useLazyGetDocumentEditorConfigQuery();
  const [isEditorLoading, setIsEditorLoading] = useState(false);

  const { data: coverage, refetch: refetchCoverage } = useGetCoverageQuery({
    matterId: matterId ?? '',
    coverageId: coverageId ?? '',
  });

  const { data: matterDetails } = useGetMatterDetailsQuery(matterId ?? '', {
    skip: !matterId,
  });

  // Fax transmissions for this matter
  const {
    data: faxTransmissionsData,
    isFetching: isFaxFetching,
    refetch: refetchFaxes,
  } = useListFaxTransmissionsByMatterQuery(matterId ?? '', {
    skip: !matterId,
  });

  const faxTransmissions = useMemo(
    () => faxTransmissionsData?.transmissions ?? [],
    [faxTransmissionsData?.transmissions],
  );
  const faxCount = faxTransmissionsData?.count ?? 0;

  const backendHost =
    (
      import.meta as unknown as {
        env?: { VITE_APP_DJANGO_BACKEND_HOST?: string };
      }
    ).env?.VITE_APP_DJANGO_BACKEND_HOST ?? '';

  const {
    lorDocs,
    isLoading: isDocsLoading,
    refetch: refetchDocs,
  } = useLorDocuments(matterId ?? '');
  const [addLorToDocuments, { isLoading: isAddingToDocuments }] =
    useAddLorToDocumentsMutation();
  const sendCardRef = useRef<HTMLDivElement | null>(null);
  const faxCardRef = useRef<SendFaxInlineCardRef | null>(null);
  const emailCardRef = useRef<SendFrontEmailInlineCardRef | null>(null);

  const uploadedArtifact = coverage?.lor_uploaded_artifact;
  const hasUploadedLor = Boolean(
    coverage?.lor_uploaded_artifact_id && uploadedArtifact,
  );
  const showUploadedLorChip =
    hasUploadedLor && Boolean(matterId && coverageId && uploadedArtifact);
  const preferredDocId = useMemo(() => {
    if (coverage?.lor_document_id) {
      const exists = lorDocs.some((doc) => doc.id === coverage.lor_document_id);
      if (exists) {
        return coverage.lor_document_id;
      }
    }
    // Only fall back to docs that belong to this coverage (or have no coverage set)
    const docsForCoverage = coverageId
      ? lorDocs.filter(
          (doc) => !doc.coverage_id || doc.coverage_id === coverageId,
        )
      : lorDocs;
    return docsForCoverage[0]?.id ?? null;
  }, [coverage?.lor_document_id, lorDocs, coverageId]);

  useEffect(() => {
    setSelectedDocId((prev) => {
      if (prev === preferredDocId) return prev;
      return preferredDocId ?? null;
    });
  }, [preferredDocId]);

  const selectedDoc = useMemo(
    () => lorDocs.find((doc) => doc.id === selectedDocId) || null,
    [lorDocs, selectedDocId],
  );
  // Always fetch the single document fresh to get the latest field_values
  // (which includes delivery_method from "Send for Review")
  const singleDocQueryArg =
    !hasUploadedLor && matterId && selectedDocId
      ? { matterId, documentId: selectedDocId }
      : skipToken;
  const { data: fetchedSelectedDoc } =
    useGetMatterDocumentQuery(singleDocQueryArg, {
      refetchOnMountOrArgChange: true,
    });
  // Prefer freshly fetched document over list data for field_values
  const docForMetadata = fetchedSelectedDoc ?? selectedDoc ?? null;
  const selectedDocFieldValues = docForMetadata?.field_values as
    | DocumentFieldValues
    | null
    | undefined;
  const savedEmailBody = useMemo(() => {
    const raw = selectedDocFieldValues?.lor_email_body;
    return typeof raw === 'string' ? raw : '';
  }, [selectedDocFieldValues?.lor_email_body]);

  // Delivery method preferences stored when "Send for Review" was clicked
  const savedDeliveryMethod = useMemo(() => {
    const raw = selectedDocFieldValues?.lor_delivery_method;
    return raw === 'email' || raw === 'fax' ? raw : null;
  }, [selectedDocFieldValues?.lor_delivery_method]);

  const savedDeliveryRecipient = useMemo(() => {
    const raw = selectedDocFieldValues?.lor_delivery_recipient;
    return typeof raw === 'string' ? raw : '';
  }, [selectedDocFieldValues?.lor_delivery_recipient]);

  // Fax cover page fields from delivery method picker
  const savedFaxCoverTo = useMemo(() => {
    const raw = selectedDocFieldValues?.lor_fax_cover_to;
    return typeof raw === 'string' ? raw : '';
  }, [selectedDocFieldValues?.lor_fax_cover_to]);

  const savedFaxCoverFrom = useMemo(() => {
    const raw = selectedDocFieldValues?.lor_fax_cover_from;
    return typeof raw === 'string' ? raw : '';
  }, [selectedDocFieldValues?.lor_fax_cover_from]);

  const savedAdditionalMatterFiles = useMemo(() => {
    const raw = selectedDocFieldValues?.lor_email_additional_files;
    if (!Array.isArray(raw)) {
      return [];
    }
    return raw
      .map((file) => {
        const candidate = file as {
          blob_id?: unknown;
          id?: unknown;
          filename?: unknown;
        };
        // Handle both id (legacy/direct) and blob_id (nested schema)
        let blobId: string | null = null;
        if (typeof candidate.blob_id === 'string') {
          blobId = candidate.blob_id;
        } else if (typeof candidate.id === 'string') {
          blobId = candidate.id;
        }

        const filename =
          typeof candidate.filename === 'string' ? candidate.filename : null;
        if (!blobId || !filename) {
          return null;
        }
        return { id: blobId, filename };
      })
      .filter(
        (
          file,
        ): file is {
          id: string;
          filename: string;
        } => Boolean(file),
      );
  }, [selectedDocFieldValues?.lor_email_additional_files]);
  const hasGeneratedLorDocument =
    Boolean(coverage?.lor_document_id) || lorDocs.length > 0;
  const missingCoverageDoc =
    !hasUploadedLor && !hasGeneratedLorDocument && !isDocsLoading;
  const isInitialLorLoading =
    !hasUploadedLor && !hasGeneratedLorDocument && isDocsLoading;

  const {
    url: previewUrl,
    request: requestPreview,
    isLoading: isPreviewLoading,
    error: previewError,
  } = useLorPreview(matterId ?? '', selectedDocId);
  useEffect(() => {
    if (selectedDocId) {
      requestPreview();
    }
  }, [requestPreview, selectedDocId]);
  const activePreviewUrl = previewUrl;
  const previewErrorState = previewError;
  const previewLoadingState = isPreviewLoading;
  const writeLorUrl = useMemo(
    () =>
      buildClaimsFilingUrl(
        matterId ?? '',
        coverageId ?? '',
        'lor' as ClaimFilingStep,
      ),
    [matterId, coverageId],
  );
  const formatApiError = useCallback((error: unknown, fallback: string) => {
    if (error && typeof error === 'object' && 'data' in error) {
      const { data } = error as { data?: { error?: string; detail?: string } };
      if (data?.error && typeof data.error === 'string') return data.error;
      if (data?.detail && typeof data.detail === 'string') return data.detail;
    }
    if (error instanceof Error && error.message) {
      return error.message;
    }
    return fallback;
  }, []);
  const handleCitationHighlightChange = useCallback(
    (blockId: string) => {
      if (!blockId) {
        handleSelectCitation(null);
        return;
      }

      const current = agentChatContext?.selectedCitation ?? standaloneCitation;
      if (current) {
        handleSelectCitation({
          ...current,
          selectedBlockId: blockId,
        });
      }
    },
    [agentChatContext, handleSelectCitation, standaloneCitation],
  );
  const hasAnyLorDocument = hasUploadedLor || hasGeneratedLorDocument;

  const handleAddLorToDocuments = useCallback(async () => {
    if (!matterId || !coverageId || !hasAnyLorDocument) return;
    try {
      const result = await addLorToDocuments({
        matterId,
        coverageId,
      }).unwrap();
      if (result.status === 'already_exists') {
        toast.info('LOR already exists in documents');
      } else {
        toast.success('LOR added to documents');
      }
    } catch {
      toast.error('Failed to add LOR to documents');
    }
  }, [addLorToDocuments, coverageId, hasAnyLorDocument, matterId]);

  const { data: tasksResponse, refetch: refetchTasks } = useGetTasksQuery(
    {
      matter: matterId ?? '',
      limit: 200,
    },
    {
      refetchOnMountOrArgChange: true,
    },
  );

  const tasks = useMemo(
    () => tasksResponse?.results ?? [],
    [tasksResponse?.results],
  );

  const stepTask = useMemo(() => {
    return getLatestCoverageTaskByCanonicalType(
      tasks,
      coverageId ?? '',
      CanonicalTaskType.SEND_LOR,
    );
  }, [coverageId, tasks]);

  // Premises-specific delivery logic (contacts, certified mail, etc.)
  const {
    isPremisesCoverage,
    propertyContacts,
    selectedPremisesContactIdx,
    setSelectedPremisesContactIdx,
    selectedPremisesContact,
    premisesEmailRecipients,
    premisesFaxNumber,
    isPreparingCertifiedMail,
    isSendingCertifiedMail,
    certifiedMailSendStatus,
    handleSendCertifiedMail,
  } = usePremisesDelivery({
    coverage,
    matterId,
    coverageId,
    selectedDocId,
    savedDeliveryRecipient,
    refetchCoverage,
    refetchTasks,
    taskId: stepTask?.id,
  });

  const { data: taskStatuses = [] } = useGetTaskStatusesQuery();
  const [updateTask, { isLoading: isMarkingComplete }] =
    useUpdateTaskMutation();
  const [createTaskUpdate] = useCreateTaskUpdateMutation();
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [isApproving, setIsApproving] = useState(false);

  const {
    data: sendLorDeliverables,
    isFetching: isDeliverablesLoading,
    refetch: refetchDeliverables,
  } = useGetTaskDeliverablesQuery(
    {
      matterId: matterId ?? '',
      taskId: stepTask?.id ?? '',
    },
    { skip: !stepTask?.id || !matterId },
  );

  const sendLorDeliverable = useMemo(() => {
    if (!sendLorDeliverables || sendLorDeliverables.length === 0) {
      return null;
    }
    const templateMatch = sendLorDeliverables.find((deliverable) =>
      (deliverable.name || '')
        .toLowerCase()
        .includes('sent letter of representation'),
    );
    return templateMatch ?? sendLorDeliverables[0];
  }, [sendLorDeliverables]);

  const emailHistoryMatches = useMemo(() => {
    const matches = sendLorDeliverable?.matches || [];
    // Only show matches that have an associated Front email conversation
    return [...matches]
      .filter((match) => Boolean(match.front_conversation_id))
      .sort((a, b) => {
        const aDate = new Date(a.created_at || 0).getTime();
        const bDate = new Date(b.created_at || 0).getTime();
        return bDate - aDate;
      });
  }, [sendLorDeliverable?.matches]);

  const underReviewStatusId = useMemo(() => {
    const explicit = taskStatuses.find(
      (status) =>
        status.name?.toLowerCase() === 'under review' ||
        (status.canonical_status === CanonicalTaskStatus.BLOCKED &&
          status.name?.toLowerCase().includes('review')),
    );
    if (explicit) return explicit.id;
    const blocked = taskStatuses.find(
      (status) => status.canonical_status === CanonicalTaskStatus.BLOCKED,
    );
    return blocked?.id;
  }, [taskStatuses]);

  const isUnderReview = useMemo(() => {
    if (!stepTask?.status) return false;
    if (stepTask.status.name?.toLowerCase() === 'under review') return true;
    if (!underReviewStatusId) return false;
    return stepTask.status.id === underReviewStatusId;
  }, [stepTask, underReviewStatusId]);

  const completedStatusId = useMemo(() => {
    return (
      taskStatuses.find(
        (status) => status.canonical_status === CanonicalTaskStatus.COMPLETED,
      )?.id ?? null
    );
  }, [taskStatuses]);

  const pendingStatusId = useMemo(() => {
    return (
      taskStatuses.find(
        (status) => status.canonical_status === CanonicalTaskStatus.PENDING,
      )?.id ?? null
    );
  }, [taskStatuses]);

  const inProgressStatusId = useMemo(() => {
    return (
      taskStatuses.find(
        (status) => status.canonical_status === CanonicalTaskStatus.IN_PROGRESS,
      )?.id ?? null
    );
  }, [taskStatuses]);

  // Combined handler that sends and marks task complete
  const handleSendComplete = useCallback(async () => {
    // Await the refetch to ensure communication history updates immediately
    await refetchDeliverables();
    // Mark task as complete after successful send
    if (matterId && stepTask && completedStatusId) {
      try {
        await updateTask({
          matterId,
          taskId: stepTask.id,
          task: {
            status_id: completedStatusId,
            completed_at: new Date().toISOString(),
          },
        }).unwrap();
        toast.success('LOR sent and task marked complete');
      } catch (error) {
        const errorData = (error as { data?: { code?: string; error?: string } })?.data;
        if (errorData?.code === 'PREMISES_LOR_CONTACT_REQUIRED') {
          toast.error(
            errorData.error ||
              'Cannot complete Send LOR: a property contact with a valid mailing address is required before sending.',
          );
        } else {
          toast.error('Sent LOR but failed to mark task complete');
        }
      }
    }
  }, [refetchDeliverables, matterId, stepTask, completedStatusId, updateTask]);

  // Trigger send from the appropriate card (email or fax)
  // Also adds LOR to matter documents when approving
  const handleApproveAndSend = useCallback(async () => {
    setIsApproving(true);
    try {
      // Premises property coverages always send via certified mail.
      if (isPremisesCoverage) {
        await handleSendCertifiedMail();
        // Transition task out of "under review" so StepActionHeader shows
        // the premises post-approval UI. Don't mark completed — that happens
        // when correspondence confirms mail sent.
        if (matterId && stepTask && inProgressStatusId) {
          try {
            await updateTask({
              matterId,
              taskId: stepTask.id,
              task: { status_id: inProgressStatusId },
            }).unwrap();
          } catch {
            // Non-critical: certified mail was already queued
            console.error('Failed to update task status after certified mail queued');
          }
        }
        return;
      }

      // Add LOR to matter documents (fire and forget - don't block send on this)
      if (matterId && coverageId && hasAnyLorDocument) {
        addLorToDocuments({ matterId, coverageId }).catch(() => {
          // Silently fail - the send is the primary action
        });
      }

      if (savedDeliveryMethod === 'fax') {
        await faxCardRef.current?.triggerSend();
      } else {
        await emailCardRef.current?.triggerSend();
      }
    } catch (error) {
      toast.error(
        formatApiError(
          error,
          'Failed to send LOR',
        ),
      );
    } finally {
      setIsApproving(false);
    }
  }, [
    isPremisesCoverage,
    handleSendCertifiedMail,
    matterId,
    coverageId,
    savedDeliveryMethod,
    addLorToDocuments,
    hasAnyLorDocument,
    inProgressStatusId,
    stepTask,
    updateTask,
  ]);

  const handleReject = useCallback(() => {
    setIsRejectModalOpen(true);
  }, []);

  const handleRejectConfirm = useCallback(
    async (feedback: string) => {
      if (!matterId || !stepTask || !pendingStatusId) return;
      try {
        // Create a task update with the rejection feedback
        await createTaskUpdate({
          matterId,
          taskId: stepTask.id,
          update: {
            update: `**LOR Rejected:** ${feedback}`,
            visibility: TaskUpdateVisibility.PRIVATE,
          },
        }).unwrap();

        // Update the task status back to pending
        await updateTask({
          matterId,
          taskId: stepTask.id,
          task: {
            status_id: pendingStatusId,
            completed_at: undefined,
          },
        }).unwrap();

        toast.success('Returned LoR task to To Do with feedback');
      } catch {
        toast.error('Failed to reject LOR');
        throw new Error('Failed to reject LOR');
      }
    },
    [createTaskUpdate, matterId, pendingStatusId, stepTask, updateTask],
  );

  const selectedAttachment = useMemo(() => {
    if (!selectedDoc) return [];
    const label =
      selectedDoc.document_template?.name || 'Letter of Representation';
    // Prefer the standardized LOR filename from the backend (naming convention)
    const filename = selectedDoc.lor_filename || (label.match(/\.(pdf|docx)$/i) ? label : `${label}.pdf`);
    return [
      {
        id: selectedDoc.id,
        filename,
        label,
      },
    ];
  }, [selectedDoc]);
  const uploadedMatterFiles = useMemo(() => {
    // If we have a selected doc that matches the uploaded LOR, don't duplicate it here
    // because it is already included in selectedAttachment
    if (
      hasUploadedLor &&
      selectedDoc &&
      selectedDoc.id === coverage?.lor_document_id
    ) {
      return [];
    }
    if (!hasUploadedLor || !uploadedArtifact?.blob_id) {
      return [];
    }
    const filename =
      uploadedArtifact.original_filename ||
      uploadedArtifact.name ||
      'Uploaded-LOR.pdf';
    return [
      {
        id: uploadedArtifact.blob_id,
        filename,
      },
    ];
  }, [
    hasUploadedLor,
    selectedDoc,
    coverage?.lor_document_id,
    uploadedArtifact,
  ]);
  const emailMatterFiles = useMemo(() => {
    const map = new Map<string, { id: string; filename: string }>();
    [...uploadedMatterFiles, ...savedAdditionalMatterFiles].forEach((file) => {
      map.set(file.id, file);
    });
    return Array.from(map.values());
  }, [savedAdditionalMatterFiles, uploadedMatterFiles]);
  const defaultEmailBody = useMemo(() => {
    if (savedEmailBody) return savedEmailBody;
    // Fallback: generate a default body from matter data
    // (mirrors backend _build_default_lor_email_body logic)
    const clientName = matterDetails?.name ?? 'our client';
    const hour = new Date().getHours();
    const salutation = hour < 12 ? 'Good morning,' : 'Good afternoon,';
    return (
      `${salutation}\n\n` +
      `Please see the attached letter of representation for our client, ${clientName}.\n\n` +
      'Please provide our office with a copy of the acknowledgment letter ' +
      'and requested policy documents at your earliest convenience.\n\n' +
      'Should you have any questions or concerns please do not hesitate to reach out.\n\n' +
      'Thank you, and have a wonderful day.'
    );
  }, [savedEmailBody, matterDetails?.name]);

  // Fax number from adjuster or carrier (used by FaxWizard)
  const fallbackAdjusterFax = useMemo(() => {
    const adjusters = coverage?.adjusters as
      | { handles?: ContactHandleSchema[] }[]
      | undefined;
    if (!adjusters?.length) return '';
    const handles = adjusters[0].handles ?? [];
    return (
      handles.find((h: ContactHandleSchema) =>
        (h.handle_type || '').toLowerCase().includes('fax'),
      )?.value ?? ''
    );
  }, [coverage?.adjusters]);

  const fallbackCarrierFax = useMemo(() => {
    const handles = coverage?.carrier?.handles ?? [];
    return (
      handles.find((h: ContactHandleSchema) =>
        (h.handle_type || '').toLowerCase().includes('fax'),
      )?.value ?? ''
    );
  }, [coverage?.carrier?.handles]);

  const faxRecipientNumber = fallbackAdjusterFax || fallbackCarrierFax || '';

  const defaultEmailRecipients = useMemo(() => {
    // Use saved delivery recipient from the previous step or backend metadata
    if (savedDeliveryRecipient.trim()) {
      return savedDeliveryRecipient
        .split(',')
        .map((r) => r.trim())
        .filter(Boolean);
    }
    // Fallback: derive from coverage adjuster/carrier email handles
    // (matches backend _first_handle_value logic in set_lor_default_delivery_metadata_activity)
    const adjusters = coverage?.adjusters as
      | { handles?: ContactHandleSchema[] }[]
      | undefined;
    const adjusterEmail = adjusters?.[0]?.handles?.find(
      (h: ContactHandleSchema) =>
        (h.handle_type || '').toUpperCase().includes('EMAIL'),
    )?.value?.trim();
    if (adjusterEmail) return [adjusterEmail];

    const carrierHandles = (coverage?.carrier as { handles?: ContactHandleSchema[] })
      ?.handles;
    const carrierEmail = carrierHandles?.find(
      (h: ContactHandleSchema) =>
        (h.handle_type || '').toUpperCase().includes('EMAIL'),
    )?.value?.trim();
    if (carrierEmail) return [carrierEmail];

    return [];
  }, [savedDeliveryRecipient, coverage?.adjusters, coverage?.carrier]);

  // Use premises-specific recipients if applicable
  const effectiveEmailRecipients = isPremisesCoverage
    ? premisesEmailRecipients.length > 0
      ? premisesEmailRecipients
      : defaultEmailRecipients
    : defaultEmailRecipients;

  const effectiveFaxNumber = isPremisesCoverage
    ? premisesFaxNumber || faxRecipientNumber
    : faxRecipientNumber;

  const defaultEmailSubject = useMemo(() => {
    return (
      (coverage?.claims && coverage.claims.length > 0
        ? coverage.claims[0]?.claim_identifier
        : null) ?? ''
    );
  }, [coverage]);

  const downloadWord = () => {
    if (!matterId || !selectedDoc) return;
    const url = `${backendHost}/api/matters/${matterId}/documents/${selectedDoc.id}/download/`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };
  const downloadUploadedLor = () => {
    // Prefer using the DocumentInstance download if available (handles both types robustly)
    if (selectedDoc && selectedDoc.id === coverage?.lor_document_id) {
      downloadWord();
      return;
    }
    if (!matterId || !coverageId || !hasUploadedLor) return;
    const url = `${backendHost}/api/matters/${matterId}/coverages/${coverageId}/upload-lor/download/`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleEditDocument = useCallback(async () => {
    const docId = selectedDocId;
    if (!docId || !matterId) return;
    setIsEditorLoading(true);
    try {
      const config = await getDocumentEditorConfig({
        matterId,
        documentId: docId,
      }).unwrap();
      setEditorConfig(config);
    } catch {
      toast.error('Failed to load document editor');
    } finally {
      setIsEditorLoading(false);
    }
  }, [getDocumentEditorConfig, matterId, selectedDocId]);

  const handleEditorClose = useCallback(() => {
    setEditorConfig(null);
    // Re-request preview to pick up any changes made in the editor
    if (selectedDocId) {
      requestPreview();
    }
  }, [requestPreview, selectedDocId]);

  const handleOpenPreviewDialog = useCallback(() => {
    setIsPreviewDialogOpen(true);
    if (selectedDocId) {
      requestPreview();
    }
  }, [requestPreview, selectedDocId]);

  const handleDownloadPreviewPdf = useCallback(() => {
    if (!activePreviewUrl) return;
    window.open(activePreviewUrl, '_blank', 'noopener,noreferrer');
  }, [activePreviewUrl]);

  // Use the shared utility to parse potentially raw LLM output
  const parsedLorReview = useMemo(() => {
    const raw = selectedDocFieldValues?.lor_review_html;
    return parseLorReviewTable(raw);
  }, [selectedDocFieldValues?.lor_review_html]);

  const reviewHtml = useMemo(() => {
    let raw = selectedDocFieldValues?.lor_review_html;

    // If we successfully parsed raw LLM output, use the remaining text
    if (parsedLorReview) {
      raw = parsedLorReview.remainingContent;
    }

    if (typeof raw !== 'string' || !raw.trim()) return null;

    // The summary often contains citations as escaped HTML entities within
    // JSON array strings (e.g. **Citations:** ["&lt;cite...&gt;"]).
    // These can't be rendered as interactive pills, so we clean them up.
    // The actual citations are in lor_review_rows with proper citation-ids.
    let cleaned = raw;

    // Remove citation array sections like: - **Citations:** [...] or **Citations:** [...]
    cleaned = cleaned.replace(
      /[-\s]*\*?\*?Citations\*?\*?:?\s*\[[^\]]*\]/gi,
      '',
    );

    // Clean up any remaining escaped cite tags that appear as text
    cleaned = cleaned.replace(/&lt;cite[^&]*&gt;(&lt;\/cite&gt;)?/gi, '');
    cleaned = cleaned.replace(/<cite[^>]*>(<\/cite>)?/gi, '');

    // Clean up empty list items and extra whitespace
    cleaned = cleaned.replace(/<p>\s*<\/p>/g, '');
    cleaned = cleaned.replace(/\n{3,}/g, '\n\n');

    return cleaned;
  }, [selectedDocFieldValues?.lor_review_html, parsedLorReview]);

  const htmlFallbackRows = useMemo(() => {
    if (!reviewHtml) return null;
    // Fallback to parsing rendered HTML table if present
    return extractLorReviewRowsFromHtml(reviewHtml);
  }, [reviewHtml]);

  const structuredLorReviewRows = useMemo(() => {
    const rawValue = selectedDocFieldValues?.lor_review_rows;
    if (rawValue) {
      // If we have structured rows from DB, use them (assuming they are array)
      if (Array.isArray(rawValue)) return rawValue;
    }
    // If no structured rows in DB, check if we parsed them from raw text
    if (parsedLorReview?.rows) {
      return parsedLorReview.rows;
    }
    return null;
  }, [selectedDocFieldValues?.lor_review_rows, parsedLorReview]);

  const lorReviewRows = useMemo(() => {
    if (structuredLorReviewRows?.length) {
      const normalized = structuredLorReviewRows
        .map((row, index) => normalizeLorReviewRow(row, index))
        .filter((row): row is NormalizedLorReviewRow => Boolean(row));
      if (normalized.length > 0) {
        return normalized;
      }
    }
    return htmlFallbackRows;
  }, [htmlFallbackRows, structuredLorReviewRows]);

  const showInlinePreview = !isPreviewDialogOpen;
  const pageBackgroundClass =
    'min-h-screen bg-gradient-to-b from-slate-50 via-white to-white';
  const contentWrapperClass =
    'mx-auto w-full max-w-[2200px] space-y-8 px-8 py-6';

  const formatDisplayDate = (value?: string | null): string | null => {
    if (!value) return null;
    try {
      return new Date(value).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      });
    } catch {
      return null;
    }
  };

  let previewTitle = 'Latest Letter of Representation';
  if (hasUploadedLor) {
    previewTitle = 'Uploaded Letter of Representation';
  } else if (selectedDoc) {
    previewTitle = 'Selected Letter of Representation';
  }

  let previewTimestampLabel = 'No documents generated yet';
  if (hasUploadedLor && uploadedArtifact?.created_at) {
    previewTimestampLabel =
      formatDisplayDate(uploadedArtifact.created_at) ??
      'Uploaded document timestamp unavailable';
  } else if (selectedDoc) {
    previewTimestampLabel =
      formatDisplayDate(selectedDoc.created_at) ??
      'Generation timestamp unavailable';
  } else if (coverage?.lor_document_id) {
    previewTimestampLabel = 'Selected LoR document is unavailable';
  }

  let uploadedFileLabel: string | null = null;
  if (hasUploadedLor) {
    uploadedFileLabel =
      uploadedArtifact?.original_filename ||
      uploadedArtifact?.name ||
      'Uploaded-LOR';
  }

  let templateLabel: string | null = null;
  if (!hasUploadedLor && selectedDoc?.document_template?.name) {
    templateLabel = selectedDoc.document_template.name;
  }

  const renderPreviewMeta = (variant: 'page' | 'modal') => (
    <>
      {uploadedFileLabel && (
        <div
          className={`${
            variant === 'page' ? 'text-sm' : 'text-xs'
          } border-b border-zinc-100 bg-slate-50/70 px-4 py-2 text-zinc-600`}
        >
          File:{' '}
          <span className="font-medium text-zinc-900">{uploadedFileLabel}</span>
        </div>
      )}
      {templateLabel && (
        <div
          className={`${
            variant === 'page' ? 'text-sm' : 'text-xs'
          } border-b border-zinc-100 bg-slate-50/70 px-4 py-2 text-zinc-600`}
        >
          Template:{' '}
          <span className="font-medium text-zinc-900">{templateLabel}</span>
        </div>
      )}
    </>
  );

  const renderPreviewFrame = ({
    containerClass,
    forceInline = false,
  }: {
    containerClass: string;
    forceInline?: boolean;
  }) => {
    // We now support preview for uploaded LORs via the DocumentInstancePreviewView
    // so we don't need to block rendering here.
    const shouldShowInline = forceInline || showInlinePreview;
    if (previewErrorState) {
      return (
        <div
          className={`${containerClass} flex flex-col items-center justify-center gap-3 bg-zinc-50 text-center`}
        >
          <p className="text-sm text-red-600">
            Failed to load the Letter of Representation preview.
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              if (selectedDocId) {
                requestPreview();
              }
            }}
          >
            Retry preview
          </Button>
        </div>
      );
    }
    if (isDocsLoading || previewLoadingState) {
      return (
        <div
          className={`${containerClass} flex items-center justify-center bg-white`}
        >
          <p className="text-sm text-zinc-500">Loading preview…</p>
        </div>
      );
    }
    if (shouldShowInline && activePreviewUrl) {
      // Append PDF viewer parameters to hide sidebar and show only the document
      const pdfUrlWithParams = `${activePreviewUrl}#navpanes=0`;
      return (
        <div className={containerClass}>
          <object
            className="h-full w-full"
            data={pdfUrlWithParams}
            type="application/pdf"
          >
            <p className="p-4 text-sm text-zinc-500">
              Preview is not available.{' '}
              <button
                type="button"
                onClick={() => window.open(activePreviewUrl, '_blank')}
                className="text-blue-600 underline"
              >
                Open in new tab
              </button>
            </p>
          </object>
        </div>
      );
    }
    if (shouldShowInline) {
      let emptyStateMessage = 'No Letter of Representation generated yet.';
      if (coverage?.lor_document_id && !selectedDoc && lorDocs.length > 0) {
        emptyStateMessage = 'Selected Letter of Representation is unavailable.';
      }
      return (
        <div
          className={`${containerClass} flex items-center justify-center bg-white`}
        >
          <p className="text-sm text-zinc-500">{emptyStateMessage}</p>
        </div>
      );
    }
    return (
      <div
        className={`${containerClass} flex items-center justify-center bg-white`}
      >
        <p className="text-sm text-zinc-500">
          Preview is rendered in the modal.
        </p>
      </div>
    );
  };

  // Combined communication history (emails + faxes) sorted by date
  type CommunicationItem =
    | { type: 'email'; data: (typeof emailHistoryMatches)[0]; date: Date }
    | { type: 'fax'; data: FaxTransmission; date: Date };

  const communicationHistory = useMemo(() => {
    const items: CommunicationItem[] = [];

    // Add emails
    emailHistoryMatches.forEach((match) => {
      items.push({
        type: 'email',
        data: match,
        date: new Date(match.created_at || 0),
      });
    });

    // Add faxes
    faxTransmissions.forEach((fax) => {
      items.push({
        type: 'fax',
        data: fax,
        date: new Date(fax.created_at),
      });
    });

    // Sort by date descending (newest first)
    return items.sort((a, b) => b.date.getTime() - a.date.getTime());
  }, [emailHistoryMatches, faxTransmissions]);

  const formatPhoneNumber = (phoneNumber: string | null | undefined) => {
    const source = phoneNumber ?? '';
    const cleaned = String(source).replace(/\D/g, '');
    if (cleaned.length === 11 && cleaned.startsWith('1')) {
      return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
    }
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    return String(source);
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status.toLowerCase()) {
      case 'delivered':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'sent':
      case 'sending':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'failed':
      case 'error':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-zinc-100 text-zinc-800 border-zinc-200';
    }
  };

  const emailHistorySection = (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold text-zinc-700">
          Communication History
        </span>
        {(isDeliverablesLoading || isFaxFetching) && (
          <span className="text-xs text-zinc-500">Refreshing…</span>
        )}
      </div>
      <div className="space-y-3">
        {communicationHistory.length === 0 ? (
          <p className="rounded-xl border border-dashed border-zinc-200 bg-zinc-50 px-3 py-2 text-sm text-zinc-500">
            Emails and faxes sent will be logged here for easy reference.
          </p>
        ) : (
          communicationHistory.map((item) =>
            item.type === 'email' ? (
              <DeliverableMatchCard
                key={`email-${item.data.id}`}
                match={item.data}
                matterId={matterId ?? ''}
                showInspectButton={false}
                setSelectedCitation={handleSelectCitation}
                variant="simple"
              />
            ) : (
              <div
                key={`fax-${item.data.id}`}
                className="rounded-xl border border-zinc-200 bg-white p-3"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-zinc-500">
                      📠 Fax
                    </span>
                    <span className="text-sm text-zinc-700">
                      {formatPhoneNumber(item.data.recipient_number)}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span
                      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium capitalize ${getStatusBadgeClass(item.data.status)}`}
                    >
                      {item.data.status}
                    </span>
                    <span className="text-xs text-zinc-500">
                      {format(item.date, 'MMM d, h:mm a')}
                    </span>
                  </div>
                </div>
                {item.data.file_name && (
                  <p className="mt-1 text-xs text-zinc-500 truncate">
                    {item.data.file_name}
                  </p>
                )}
              </div>
            ),
          )
        )}
      </div>
    </div>
  );

  if (isInitialLorLoading) {
    return (
      <div className={pageBackgroundClass}>
        <div className={contentWrapperClass}>
          <div className="space-y-4">
            <StepActionHeader
              step="review_send"
              matterId={matterId ?? ''}
              coverageId={coverageId ?? ''}
            />
            <div className="rounded-3xl border border-zinc-100 bg-white/80 p-6 text-sm text-zinc-600 shadow-inner shadow-slate-100/70">
              Loading Letter of Representation data…
            </div>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className={pageBackgroundClass}>
      <div className={contentWrapperClass}>
        <section className="space-y-4">
          {!hideHeader && (
            <StepActionHeader
              step="review_send"
              matterId={matterId ?? ''}
              coverageId={coverageId ?? ''}
            />
          )}
          {missingCoverageDoc && (
            <div className="rounded-3xl border border-red-100 bg-white/80 p-6 text-sm text-red-800 shadow-xl shadow-red-200/60">
              <div className="flex items-center gap-2 text-base font-semibold">
                <span role="img" aria-label="warning">
                  ⚠️
                </span>
                No Letter of Representation is linked to this coverage yet.
              </div>
              <p className="mt-3 text-zinc-600">
                Generate a Letter of Representation on the previous step. You
                can still compose an email and review FinchGPT output while you
                work on the LoR.
              </p>
              <div className="mt-4">
                <Link
                  to={writeLorUrl}
                  className="inline-flex items-center rounded-full border border-red-200 bg-white/80 px-3 py-1.5 text-sm font-semibold text-red-700 shadow-sm shadow-red-200/50 transition hover:bg-red-50"
                >
                  Write a Letter of Representation
                </Link>
              </div>
            </div>
          )}
          <div className="rounded-2xl border border-white/70 bg-white/90 p-4 shadow-inner shadow-slate-100/70">
            <div className="flex flex-wrap items-center gap-3">
              <Button
                size="lg"
                variant="secondary"
                onClick={hasUploadedLor ? downloadUploadedLor : downloadWord}
                disabled={hasUploadedLor ? false : !selectedDoc}
              >
                {hasUploadedLor ? 'Download uploaded LOR' : 'Download Word'}
              </Button>
              {showUploadedLorChip && uploadedArtifact ? (
                <UploadedLorFileChip
                  matterId={matterId ?? ''}
                  coverageId={coverageId ?? ''}
                  artifact={uploadedArtifact}
                  onDownload={downloadUploadedLor}
                />
              ) : (
                <Button
                  size="lg"
                  variant="outline"
                  onClick={handleOpenPreviewDialog}
                  disabled={!selectedDoc}
                >
                  View latest LOR preview
                </Button>
              )}
              {selectedDoc && !hasUploadedLor && (
                <Button
                  size="lg"
                  variant="outline"
                  onClick={handleEditDocument}
                  disabled={isEditorLoading}
                  className="gap-1.5"
                >
                  {isEditorLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Pencil className="h-4 w-4" />
                  )}
                  {isEditorLoading ? 'Loading Editor…' : 'Edit in OnlyOffice'}
                </Button>
              )}
              {hasAnyLorDocument && (
                <Button
                  size="lg"
                  variant="outline"
                  onClick={handleAddLorToDocuments}
                  disabled={isAddingToDocuments}
                >
                  {isAddingToDocuments
                    ? 'Adding to Documents…'
                    : 'Add to Matter Documents'}
                </Button>
              )}
              {isUnderReview && (
                <>
                  <Button
                    size="lg"
                    variant="default"
                    onClick={handleApproveAndSend}
                    disabled={
                      isApproving ||
                      isMarkingComplete ||
                      certifiedMailSendStatus === 'pending' ||
                      certifiedMailSendStatus === 'sending' ||
                      isPreparingCertifiedMail ||
                      isSendingCertifiedMail
                    }
                  >
                    {isApproving || isPreparingCertifiedMail || isSendingCertifiedMail ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {isPreparingCertifiedMail
                          ? 'Preparing Package...'
                          : 'Sending...'}
                      </>
                    ) : certifiedMailSendStatus === 'pending' ||
                      certifiedMailSendStatus === 'sending' ? (
                      'Queued for Send'
                    ) : (
                      'Approve and Send'
                    )}
                  </Button>
                  <Button
                    size="lg"
                    variant="destructive"
                    onClick={handleReject}
                    disabled={!underReviewStatusId}
                  >
                    Reject LOR
                  </Button>
                </>
              )}
            </div>
          </div>
        </section>

        {/* Flagged Fields Summary - always visible when there are issues */}
        {lorReviewRows && lorReviewRows.length > 0 && (
          <section>
            <FlaggedFieldsSummary
              rows={lorReviewRows}
              onViewAnalysis={() => setShowAnalysis(true)}
            />
          </section>
        )}

        <section className="grid gap-6 lg:grid-cols-[minmax(500px,1.2fr)_minmax(0,1fr)]">
          {/* LOR Preview or FinchGPT analysis (toggle) */}
          <div className="rounded-2xl border border-zinc-100 bg-white shadow-lg shadow-zinc-200/60">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-zinc-100 px-4 py-3">
              <div>
                <span className="text-sm font-semibold text-zinc-700">
                  {showAnalysis ? 'FinchGPT Analysis' : previewTitle}
                </span>
                <p className="text-xs text-zinc-500">
                  {showAnalysis
                    ? 'AI-generated review of the document'
                    : 'Review the document before sending'}
                </p>
              </div>
              <div className="flex items-center gap-3">
                {!showAnalysis && (
                  <span className="text-xs text-zinc-500">
                    {previewTimestampLabel}
                  </span>
                )}
                {(reviewHtml || lorReviewRows?.length) && (
                  <div className="flex items-center rounded-lg border border-zinc-200 bg-zinc-50 p-0.5">
                    <button
                      type="button"
                      onClick={() => setShowAnalysis(false)}
                      className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                        !showAnalysis
                          ? 'bg-white text-zinc-900 shadow-sm'
                          : 'text-zinc-500 hover:text-zinc-700'
                      }`}
                    >
                      Preview
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowAnalysis(true)}
                      className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                        showAnalysis
                          ? 'bg-white text-zinc-900 shadow-sm'
                          : 'text-zinc-500 hover:text-zinc-700'
                      }`}
                    >
                      Analysis
                    </button>
                  </div>
                )}
              </div>
            </div>

            {showAnalysis ? (
              /* FinchGPT Analysis View */
              <div className="h-[70vh] min-h-[500px] overflow-auto p-6">
                {reviewHtml && (
                  <div className="mb-6">
                    <div className="mb-3 text-sm font-semibold text-zinc-700">
                      Review Summary
                    </div>
                    <div className="prose prose-sm max-w-none [&_.tableWrapper]:overflow-x-auto [&_table]:min-w-full [&_table]:border-collapse [&_th]:border [&_th]:border-zinc-200 [&_th]:bg-zinc-50 [&_th]:px-3 [&_th]:py-2 [&_th]:text-left [&_th]:text-xs [&_th]:font-semibold [&_td]:border [&_td]:border-zinc-200 [&_td]:px-3 [&_td]:py-2 [&_td]:text-sm">
                      <MessageContentViewer
                        content={reviewHtml}
                        extensions={[
                          Citations.configure({
                            matterId: matterId ?? '',
                          }),
                        ]}
                      />
                    </div>
                  </div>
                )}
                {lorReviewRows?.length ? (
                  <div>
                    <div className="mb-3 text-sm font-semibold text-zinc-700">
                      Field Comparison
                    </div>
                    <LorReviewComparisonTable
                      rows={lorReviewRows}
                      matterId={matterId ?? ''}
                      setSelectedCitation={handleSelectCitation}
                    />
                  </div>
                ) : null}
                {!reviewHtml && !lorReviewRows?.length && (
                  <div className="flex h-full items-center justify-center text-sm text-zinc-500">
                    No FinchGPT analysis available for this document.
                  </div>
                )}
              </div>
            ) : (
              /* PDF Preview View */
              <>
                {renderPreviewMeta('page')}
                {renderPreviewFrame({
                  containerClass:
                    'h-[70vh] min-h-[500px] overflow-hidden rounded-b-2xl bg-white',
                })}
              </>
            )}
          </div>

          {/* Secondary column: Send composition (Fax or Email) */}
          <div ref={sendCardRef} className="space-y-4">
            {/* Premises Contact Selector - only shown for premises coverages with multiple contacts */}
            {isPremisesCoverage && (
              <PremisesContactSelector
                contacts={propertyContacts}
                selectedIdx={selectedPremisesContactIdx}
                onSelect={setSelectedPremisesContactIdx}
              />
            )}

            {/* Premises: certified mail delivery */}
            {isPremisesCoverage && <PremisesDeliveryMethodCard />}

            {/* Certified Mail card (premises only) */}
            {isPremisesCoverage ? (
              <PremisesSendMethodCard
                selectedContact={selectedPremisesContact}
                isPreparingCertifiedMail={isPreparingCertifiedMail}
                isSendingCertifiedMail={isSendingCertifiedMail}
                certifiedMailSendStatus={certifiedMailSendStatus}
                emailHistorySection={emailHistorySection}
              />
            ) : savedDeliveryMethod === 'fax' ? (
              <SendFaxInlineCard
                key={
                  isPremisesCoverage
                    ? `fax-contact-${selectedPremisesContactIdx}`
                    : 'fax-default'
                }
                ref={faxCardRef}
                matterId={matterId ?? ''}
                documentId={docForMetadata?.id ?? ''}
                defaultRecipient={
                  isPremisesCoverage
                    ? effectiveFaxNumber
                    : savedDeliveryRecipient
                }
                defaultCoverTo={savedFaxCoverTo}
                defaultCoverFrom={savedFaxCoverFrom}
                defaultCoverMessage={savedEmailBody}
                onSent={handleSendComplete}
                isExternalLoading={isMarkingComplete}
                footerContent={emailHistorySection}
              />
            ) : (
              <SendFrontEmailInlineCard
                key={
                  isPremisesCoverage
                    ? `email-contact-${selectedPremisesContactIdx}`
                    : 'email-default'
                }
                ref={emailCardRef}
                className="h-auto"
                matterId={matterId ?? ''}
                defaultTo={effectiveEmailRecipients}
                defaultSubject={defaultEmailSubject}
                defaultBody={defaultEmailBody}
                initialLorDocs={selectedAttachment}
                initialMatterFiles={emailMatterFiles}
                taskId={stepTask?.id ?? null}
                deliverableId={sendLorDeliverable?.id ?? null}
                onSent={handleSendComplete}
                footerContent={emailHistorySection}
                isExternalLoading={isMarkingComplete}
                headerContent={{
                  subtitle: isPremisesCoverage
                    ? 'Email property contact via Front'
                    : 'Email insurer via Front',
                  title: 'Send Letter of Representation',
                  description:
                    'Messages send from your firm Front channel and automatically log to this coverage.',
                }}
              />
            )}
          </div>
        </section>

        <LorPreviewDialog
          open={isPreviewDialogOpen}
          onOpenChange={setIsPreviewDialogOpen}
          isLoading={previewLoadingState}
          previewUrl={activePreviewUrl}
          error={previewErrorState}
          onDownloadDocx={hasUploadedLor ? undefined : downloadWord}
          onDownloadPdf={handleDownloadPreviewPdf}
          disableActions={!selectedDoc && !hasUploadedLor}
          showDocxButton={!hasUploadedLor}
        />
        {selectedCitation && (
          <Dialog
            open
            onOpenChange={(open) => {
              if (!open) {
                handleSelectCitation(null);
              }
            }}
          >
            <DialogContent className="w-full max-w-5xl h-[80vh]">
              <CitationViewer
                url={selectedCitation.materializedCitation.url}
                citations={selectedCitation.materializedCitation.blocks || []}
                selectedCitationId={selectedCitation.selectedBlockId}
                setSelectedCitationId={handleCitationHighlightChange}
              />
            </DialogContent>
          </Dialog>
        )}
        <Dialog open={isFaxWizardOpen} onOpenChange={setIsFaxWizardOpen}>
          <RightDialogContent className="bg-white h-[95vh] overflow-hidden flex flex-col">
            <DialogTitle className="sr-only">Send LOR via Fax</DialogTitle>
            <div className="flex-1 overflow-auto">
              <FaxWizard
                matterId={matterId ?? ''}
                initialData={{
                  recipient_number: effectiveFaxNumber || faxRecipientNumber,
                  attachment_blob_ids: [],
                  attachment_document_ids: selectedDoc ? [selectedDoc.id] : [],
                  cover_page_to: coverage?.carrier?.name ?? '',
                  cover_page_from:
                    matterDetails?.firm?.full_name ??
                    matterDetails?.firm?.name ??
                    '',
                  cover_page_message: savedEmailBody,
                }}
                documentItems={
                  selectedDoc
                    ? [
                        {
                          id: selectedDoc.id,
                          title:
                            selectedDoc.document_template?.name ||
                            'Letter of Representation',
                          createdAt: selectedDoc.created_at,
                        },
                      ]
                    : lorDocs.map((d) => ({
                        id: d.id,
                        title:
                          d.document_template?.name ||
                          'Letter of Representation',
                        createdAt: d.created_at,
                      }))
                }
                onSuccess={async () => {
                  setIsFaxWizardOpen(false);
                  toast.success('Fax sent successfully');
                  await refetchFaxes();
                }}
                onCancel={() => setIsFaxWizardOpen(false)}
              />
            </div>
          </RightDialogContent>
        </Dialog>
        <RejectLorModal
          isOpen={isRejectModalOpen}
          onClose={() => setIsRejectModalOpen(false)}
          onConfirm={handleRejectConfirm}
        />
      </div>
      {editorConfig && (
        <OnlyOfficeEditor
          editorConfig={editorConfig}
          onClose={handleEditorClose}
          title={selectedDoc?.document_template?.name || 'Letter of Representation'}
        />
      )}
    </div>
  );
};

type DocumentFieldValues = Record<string, unknown> & {
  lor_review_html?: string;
  lor_review_rows?: unknown;
  lor_email_body?: string;
  lor_email_additional_files?: unknown[];
  lor_delivery_method?: 'email' | 'fax';
  lor_delivery_recipient?: string;
  lor_fax_cover_to?: string;
  lor_fax_cover_from?: string;
};

type RawLorReviewRow = Record<string, unknown> & {
  citations?: unknown;
};

const extractLorReviewRowsFromHtml = (
  html: string,
): NormalizedLorReviewRow[] | null => {
  if (typeof window === 'undefined' || typeof DOMParser === 'undefined') {
    return null;
  }
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const tableRows = Array.from(
      doc.querySelectorAll('.lor-review-table tbody tr'),
    );
    if (!tableRows.length) return null;

    const rawRows: RawLorReviewRow[] = tableRows.map((tr) => {
      const cells = tr.querySelectorAll('td');
      return {
        field: cells[0]?.textContent ?? undefined,
        provided_value: cells[1]?.textContent ?? undefined,
        authoritative_value: cells[2]?.textContent ?? undefined,
        status: cells[3]?.textContent ?? undefined,
        notes: cells[4]?.textContent ?? undefined,
        citations: cells[5]?.textContent ?? undefined,
      };
    });

    const normalized = rawRows
      .map((row, index) => normalizeLorReviewRow(row, index))
      .filter((row): row is NormalizedLorReviewRow => Boolean(row));

    return normalized.length ? normalized : null;
  } catch {
    return null;
  }
};
